import { getApi } from "../Services/apiService";

/**
 * The "Family Head(s) - Grand Count" report — GET
 * /report-management/family-heads-grand-count.
 *
 * The endpoint does the work: every Generation 2 head of line, oldest first,
 * with the living grand-descendants under them split into the printed report's
 * boxes — Grandson, Granddaughter, Gr Grandson, Gr Gr Granddaughter … — each
 * box's members oldest to youngest.
 *
 * Sample payload:
 *   { year: 2025, grand_total: 139, total_heads: 4,
 *     family_heads: [ { head_id, head_name: "Mary Magdalene", head_first_name,
 *       head_suffix, color_name: "Dk Blue", hex_code: "#00008B",
 *       total_grand_count: 8,
 *       categories: [ { label: "Grandson", generation_level: 4, gender: 1,
 *         count: 2, members: [ { id, first_name: "Wesley", suffix,
 *           display_name: "Wesley", dob } ] } ] } ] }
 */

const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const trimmed = (value) => (value == null ? "" : String(value).trim());

/** One payload member → the row the page renders: suffix column + first name. */
function toGrandMember(member, head) {
  const suffix = trimmed(member?.suffix);
  let firstName = trimmed(member?.first_name);
  if (!firstName) {
    // display_name leads with the suffix ("III Frank"); drop it back off.
    const display = trimmed(member?.display_name || member?.full_name);
    firstName =
      suffix && display.startsWith(`${suffix} `)
        ? display.slice(suffix.length + 1).trim()
        : display;
  }

  return {
    ...member,
    first_name: firstName,
    suffix,
    color_name: trimmed(member?.color_name) || head.colorName,
    hex_code: trimmed(member?.hex_code) || head.hexCode,
    head_id: member?.head_id ?? head.headId,
  };
}

const EMPTY_REPORT = { year: null, grandTotal: 0, heads: [] };

/**
 * The report: one entry per head, in the report's own (birth) order, each
 * with its relationship categories and the members under them.
 *
 * @param {string} familyTreeId
 * @param {{ year?: number }} [options]
 */
export async function fetchFamilyHeadsGrandCount(familyTreeId, { year = null } = {}) {
  if (!familyTreeId) return EMPTY_REPORT;

  // Heads with no living grand-descendants still come back: the page adds the
  // deceased ones off the roster, and a line may have only those.
  const params = new URLSearchParams({ family_tree_id: familyTreeId, include_empty: "true" });
  if (year != null) params.set("year", String(year));

  const response = await getApi(
    `/report-management/family-heads-grand-count?${params.toString()}`
  );

  if (response?.statusCode !== 200 || !response?.data) return EMPTY_REPORT;

  const rawHeads = Array.isArray(response.data.family_heads)
    ? response.data.family_heads
    : [];

  const heads = rawHeads
    .map((rawHead, headIndex) => {
      const head = {
        headId: rawHead?.head_id ?? null,
        headName:
          trimmed(rawHead?.head_name) ||
          [rawHead?.head_first_name, rawHead?.head_suffix].map(trimmed).filter(Boolean).join(" "),
        colorName: trimmed(rawHead?.color_name),
        hexCode: trimmed(rawHead?.hex_code),
        order: headIndex,
        raw: rawHead,
      };

      const categories = (Array.isArray(rawHead?.categories) ? rawHead.categories : [])
        .map((category) => {
          const members = (Array.isArray(category?.members) ? category.members : []).map(
            (member) => toGrandMember(member, head)
          );
          if (members.length === 0) return null;
          return {
            label: trimmed(category?.label || category?.category_name),
            generationLevel: toNumberOrNull(category?.generation_level),
            gender: toNumberOrNull(category?.gender),
            members,
          };
        })
        .filter(Boolean);

      const memberCount = categories.reduce((sum, c) => sum + c.members.length, 0);
      return {
        ...head,
        totalCount: toNumberOrNull(rawHead?.total_grand_count) ?? memberCount,
        categories,
      };
    });

  const memberTotal = heads.reduce((sum, head) => sum + head.totalCount, 0);

  return {
    year: toNumberOrNull(response.data.year) ?? year,
    grandTotal: toNumberOrNull(response.data.grand_total) ?? memberTotal,
    heads,
  };
}
